/**
 * WorkRatiosSection — carrousel des sources d'une œuvre (multi-ratio).
 *
 * Migration 015 : une œuvre peut porter N sources (un par ratio image —
 * 2:3, 5:7, 1:1…), chacun avec son propre display/thumb. Le source
 * `is_canonical=TRUE` est la version vitrine (grille publique + hero
 * fiche). L'artiste peut basculer le canonical d'un clic — la vitrine
 * change instantanément. L'éditorial Vision Tier 1 reste figé sur le
 * source qu'il a analysé (cf. `vision_source_media_id`) ; l'UI affiche
 * un badge « Analyse extraite depuis ce ratio » quand le canonical
 * actuel diffère du snapshot Vision.
 *
 * Pas de réordonnancement drag-drop dans cette PR — l'ordre est posé à
 * l'insertion (`MAX(position)+1`) et reste stable.
 */

import { useState } from 'react'
import { Star, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import AspectRatioLabel from '@/components/ui/AspectRatioLabel'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import FileDropZone from '@/components/ui/FileDropZone'
import KebabMenu, { type KebabMenuItem } from '@/components/ui/KebabMenu'
import MediaImage from '@/components/ui/MediaImage'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useAddMediaToWork, useDeleteMedia, useSetCanonicalSource } from '@/hooks/useWorks'
import type { WorkMediaResponse } from '@/types/works'

import './WorkRatiosSection.css'

interface WorkRatiosSectionProps {
  workId: number
  media: WorkMediaResponse[]
  /** Snapshot du source dont l'éditorial Vision a été extrait. NULL si
   *  Vision n'a pas tourné. */
  visionSourceMediaId: number | null
}

/** Extensions acceptées par `media_service.WORK_ALLOWED_EXTENSIONS` (JPG + RAW). */
const ACCEPT_EXTS =
  'image/jpeg,image/x-canon-cr2,image/tiff,.jpg,.jpeg,.cr2,.cr3,.nef,.arw,.dng,.raf,.orf'

export default function WorkRatiosSection({
  workId,
  media,
  visionSourceMediaId,
}: WorkRatiosSectionProps) {
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  const addMutation = useAddMediaToWork(workId)
  const setCanonicalMutation = useSetCanonicalSource(workId)
  const deleteMutation = useDeleteMedia(workId)
  const { confirm } = useConfirm()

  // Sources triés par position pour ordre stable du carrousel.
  const sources = media
    .filter((m) => m.kind === 'source')
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

  // Pour chaque source, on retrouve le display (1200×1500 WebP) qui sert
  // de vignette au carrousel — `source_media_id` lie le dérivé au parent.
  // Fallback sur le thumb si display indisponible (cas dégradé).
  const displayBySourceId = new Map<number, WorkMediaResponse>()
  const thumbBySourceId = new Map<number, WorkMediaResponse>()
  for (const m of media) {
    if (m.kind === 'display' && m.source_media_id != null) {
      displayBySourceId.set(m.source_media_id, m)
    } else if (m.kind === 'thumb' && m.source_media_id != null) {
      thumbBySourceId.set(m.source_media_id, m)
    }
  }

  // FileDropZone est controlled — on déclenche l'upload dès qu'un fichier
  // est ajouté (pas de "validation" intermédiaire pour ce flow rapide).
  const handleFilesChange = (next: File[]) => {
    setPendingFiles(next)
    if (next.length === 0) return
    addMutation.mutate(next, {
      onSuccess: () => {
        toast.success('Ratio ajouté')
        setPendingFiles([])
      },
      onError: (err) => {
        toast.error(err.detail ?? "Échec de l'ajout du ratio")
        setPendingFiles([])
      },
    })
  }

  const handleSetCanonical = (mediaId: number) => {
    setCanonicalMutation.mutate(mediaId, {
      onSuccess: () => toast.success('Vitrine mise à jour'),
      onError: (err) => toast.error(err.detail ?? 'Bascule impossible'),
    })
  }

  const handleDelete = async (mediaId: number, ratioLabel: string) => {
    const ok = await confirm({
      title: 'Supprimer ce ratio ?',
      message:
        `Le ratio ${ratioLabel} sera retiré de l'œuvre. Action irréversible — les ` +
        'fichiers display + thumb dérivés sont également purgés.',
      variant: 'danger',
      confirmText: 'Supprimer définitivement',
    })
    if (!ok) return
    deleteMutation.mutate(mediaId, {
      onSuccess: () => toast.success('Ratio supprimé'),
      onError: (err) => toast.error(err.detail ?? 'Suppression impossible'),
    })
  }

  const ratioOf = (src: WorkMediaResponse): number | null => {
    if (!src.width || !src.height) return null
    return src.width / src.height
  }

  return (
    <section className="work-ratios-section">
      <header className="work-ratios-section-header">
        <div>
          <h3 className="ui-h4 work-ratios-section-title">Versions disponibles</h3>
          <p className="ui-body ui-text-muted work-ratios-section-subtitle">
            Plusieurs ratios pour le même graphisme — l'acheteur choisit son format d'impression. La
            version « canonique » sert de vitrine (grille publique, hero fiche).
          </p>
        </div>
      </header>

      <div className="work-ratios-section-list">
        {sources.map((src) => {
          const isCanonical = src.is_canonical ?? false
          const isVisionSource = visionSourceMediaId === src.id
          const display = displayBySourceId.get(src.id)
          const thumb = thumbBySourceId.get(src.id)
          const previewMedia = display ?? thumb ?? src
          const ratio = ratioOf(src)
          const ratioLabel = ratio ? ratio.toFixed(2) : '?'

          const kebabItems: KebabMenuItem[] = [
            {
              id: 'delete',
              label: 'Supprimer ce ratio',
              icon: Trash2,
              variant: 'danger',
              disabled: deleteMutation.isPending,
              onSelect: () => {
                void handleDelete(src.id, ratioLabel)
              },
            },
          ]

          return (
            <article
              key={src.id}
              className={
                isCanonical
                  ? 'work-ratios-section-card work-ratios-section-card--canonical'
                  : 'work-ratios-section-card'
              }
            >
              <div className="work-ratios-section-card-image">
                <MediaImage minioKey={previewMedia.minio_key} alt={`Ratio ${ratioLabel}`} />
                <div className="work-ratios-section-card-actions">
                  <KebabMenu
                    ariaLabel={`Actions sur le ratio ${ratioLabel}`}
                    buttonVariant="overlay"
                    items={kebabItems}
                  />
                </div>
              </div>
              <div className="work-ratios-section-card-meta">
                <div className="work-ratios-section-card-badges">
                  <AspectRatioLabel value={ratio} />
                  {isCanonical && (
                    <Badge variant="default" size="sm">
                      <Star aria-hidden="true" size={12} />
                      Vitrine
                    </Badge>
                  )}
                  {isVisionSource && !isCanonical && (
                    <Badge
                      variant="default"
                      size="sm"
                      title="L'analyse Vision actuelle a été extraite depuis ce ratio. Le canonical a changé depuis."
                    >
                      Analyse Vision
                    </Badge>
                  )}
                </div>
                <Button
                  variant={isCanonical ? 'quiet' : 'neutral'}
                  size="sm"
                  disabled={isCanonical || setCanonicalMutation.isPending}
                  onClick={() => handleSetCanonical(src.id)}
                >
                  {isCanonical ? 'Canonique actif' : 'Définir comme canonique'}
                </Button>
              </div>
            </article>
          )
        })}

        <article className="work-ratios-section-card work-ratios-section-card--add">
          <FileDropZone
            value={pendingFiles}
            onChange={handleFilesChange}
            accept={ACCEPT_EXTS}
            maxFiles={1}
            ariaLabel="Ajouter une nouvelle version (ratio)"
          />
          {addMutation.isPending && (
            <p className="work-ratios-section-uploading ui-text-muted">Upload en cours…</p>
          )}
        </article>
      </div>
    </section>
  )
}
